"use client";

import type { ContactState } from "@/components/landing/Contact/actions";
import type { ContactLabels } from "@/components/landing/Contact/types";
import {
	faEnvelope,
	faPaperPlane,
	faPen,
	faShieldHalved,
	faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { useActionState, useEffect, useRef } from "react";
import {
	readPageId,
	readSectionMeta,
	sendLandingEvent,
} from "@/components/landing/analytics/track";
import { sendContactAction } from "@/components/landing/Contact/actions";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/Icon";

const initial: ContactState = {};

interface ContactFormProps {
	labels: ContactLabels;
	locale: string;
	/**
	 * This Contact section instance's own id — lets the server action look
	 * up its configured notify email itself. Never pass an email address
	 * through client props into the form; see `actions.ts`.
	 */
	sectionId?: string;
}

function ContactForm(prop: ContactFormProps) {
	const { labels, locale, sectionId } = prop;
	const [state, action, pending] = useActionState(sendContactAction, initial);
	const rootRef = useRef<HTMLDivElement>(null);
	const startedRef = useRef(false);

	// Fire `form_start` the first time the visitor interacts with any field.
	function handleFirstInteraction() {
		if (startedRef.current) return;
		startedRef.current = true;
		sendLandingEvent({
			eventType: "form_start",
			landingPageId: readPageId(rootRef.current),
			...readSectionMeta(rootRef.current),
		});
	}

	// Fire `form_submit` once the server action reports success.
	useEffect(() => {
		if (state.ok) {
			sendLandingEvent({
				eventType: "form_submit",
				landingPageId: readPageId(rootRef.current),
				...readSectionMeta(rootRef.current),
			});
		}
	}, [state.ok]);

	return (
		<div ref={rootRef}>
			{state.ok ? (
				<div className="card grid h-full min-h-104 place-items-center rounded-3xl p-10 text-center">
					<div>
						<div
							className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full text-2xl"
							style={{
								background: "color-mix(in srgb, var(--success) 18%, transparent)",
								color: "var(--success)",
							}}
						>
							✓
						</div>
						<p className="text-lg font-semibold">{labels.success}</p>
					</div>
				</div>
			) : (
				<form
					action={action}
					onFocus={handleFirstInteraction}
					onInput={handleFirstInteraction}
					className="card flex flex-col gap-4 p-6 sm:p-8 lg:p-10"
					style={{ borderRadius: "1.5rem" }}
				>
					<input type="hidden" name="locale" value={locale} />
					<input type="hidden" name="sectionId" value={sectionId ?? ""} />
					{state.error && (
						<p role="alert" className="rounded-lg bg-danger/15 px-3 py-2 text-sm text-danger">
							{labels.error}
						</p>
					)}
					<div className="grid gap-4 sm:grid-cols-2">
						<div className="relative min-w-0">
							<label htmlFor="contact-name" className="sr-only">
								{labels.name}
							</label>
							<span className="pointer-events-none absolute inset-y-0 start-0 grid place-items-center ps-3.5 text-muted-foreground">
								<Icon icon={faUser} size={17} />
							</span>
							<input
								id="contact-name"
								name="name"
								required
								maxLength={120}
								placeholder={labels.name}
								className="input ps-11 py-3"
							/>
						</div>
						<div className="relative min-w-0">
							<label htmlFor="contact-email" className="sr-only">
								{labels.email}
							</label>
							<span className="pointer-events-none absolute inset-y-0 start-0 grid place-items-center ps-3.5 text-muted-foreground">
								<Icon icon={faEnvelope} size={17} />
							</span>
							<input
								id="contact-email"
								name="email"
								type="email"
								required
								maxLength={160}
								placeholder={labels.email}
								className="input ps-11 py-3"
							/>
						</div>
					</div>

					<div className="relative">
						<label htmlFor="contact-subject" className="sr-only">
							{labels.subject}
						</label>
						<span className="pointer-events-none absolute inset-y-0 start-0 grid place-items-center ps-3.5 text-muted-foreground">
							<Icon icon={faEnvelope} size={17} />
						</span>
						<input
							id="contact-subject"
							name="subject"
							maxLength={160}
							placeholder={labels.subject}
							className="input ps-11 py-3"
						/>
					</div>

					<div className="relative">
						<label htmlFor="contact-message" className="sr-only">
							{labels.message}
						</label>
						<span className="pointer-events-none absolute top-0 start-0 grid pt-3.5 ps-3.5 text-muted-foreground">
							<Icon icon={faPen} size={17} />
						</span>
						<textarea
							id="contact-message"
							name="message"
							required
							maxLength={4000}
							rows={6}
							placeholder={labels.message}
							className="input resize-y ps-11 pt-3"
						/>
					</div>

					<Button
						type="submit"
						size="lg"
						disabled={pending}
						className="mt-2 h-13 gap-2 text-base font-semibold"
					>
						<Icon icon={faPaperPlane} size={18} />
						{pending ? labels.sending : labels.send}
					</Button>

					<p className="mt-2 flex items-center justify-center gap-2 text-sm text-muted-foreground">
						<Icon icon={faShieldHalved} size={15} style={{ color: "var(--brand-1)" }} />
						{labels.privacy}
					</p>
				</form>
			)}
		</div>
	);
}

export default ContactForm;
